import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, Bike, UtensilsCrossed, ReceiptText, Star, MapPin, Navigation, Loader2, Heart, Sparkles, Timer } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { foodApi, addressApi, favApi, inr } from "../../lib/api";
import { IMAGES } from "../../lib/constants";
import { useLocationCtx } from "../../context/LocationContext";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function offerCountdown(ends) {
  if (!ends) return null;
  const [y, m, d] = ends.split("-").map(Number);
  const end = new Date(y, m - 1, d, 23, 59, 59);
  const daysLeft = Math.ceil((end - new Date()) / 86400000);
  const label = end.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return { label, daysLeft };
}

export function todayHoursLabel(r) {
  if (!r?.auto_schedule || !r?.hours) return null;
  const d = r.hours[DAY_KEYS[new Date().getDay()]];
  if (!d || d.closed) return "Closed today";
  if (!d.open || !d.close) return null;
  return `${d.open} – ${d.close}`;
}

export function RestaurantCard({ r }) {
  const hours = todayHoursLabel(r);
  const closed = !r.open_now;
  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden">
        {r.image ? <img src={r.image} alt={r.name} className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${!r.open_now ? "grayscale" : ""}`} loading="lazy" /> : <div className={`h-full w-full bg-surface-200 ${!r.open_now ? "grayscale" : ""}`} data-testid={`restaurant-cover-blank-${r.restaurant_id}`} />}
        {!r.open_now && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-ink/60 px-3 text-center text-surface" data-testid={`closed-overlay-${r.restaurant_id}`}>
            <span className="text-sm font-semibold">Currently closed</span>
            {r.next_open_label && <span className="text-xs font-medium text-surface/90" data-testid={`next-open-${r.restaurant_id}`}>{r.next_open_label}</span>}
          </span>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-ink backdrop-blur">{(r.cuisines && r.cuisines[0]) || r.cuisine}</span>
        <span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur ${r.accepting_orders ? "bg-graphite-600/90 text-white" : r.open_now ? "bg-amber-400/95 text-amber-950" : "bg-white/90 text-ink-soft"}`} data-testid={`status-badge-${r.restaurant_id}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${r.accepting_orders ? "bg-emerald-300" : r.open_now ? "bg-amber-700" : "bg-red-500"}`} />{r.accepting_orders ? "Open" : r.open_now ? "Paused" : "Closed"}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-ink group-hover:text-brand">{r.name}</h3>
        <p className="mt-1 line-clamp-1 text-xs text-ink-soft">{r.description || r.address}</p>
        {r.dish_photos?.length > 0 && (
          <div className="mt-3 flex gap-2" data-testid={`dish-photos-${r.restaurant_id}`}>
            {r.dish_photos.slice(0, 3).map((p, i) => (
              <div key={i} className="h-14 flex-1 overflow-hidden rounded-lg bg-surface-200">
                <img src={p} alt={`${r.name} dish ${i + 1}`} className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
          {r.rating ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700" data-testid={`rating-${r.restaurant_id}`}><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{r.rating} <span className="font-normal text-amber-700/70">({r.rating_count})</span></span> : <span className="inline-flex items-center gap-1 text-ink-muted"><Star className="h-3.5 w-3.5" />New</span>}
          {r.distance_km != null && <span className="inline-flex items-center gap-1 font-medium text-ink-soft" data-testid={`distance-${r.restaurant_id}`}><MapPin className="h-3.5 w-3.5" />{r.distance_km} km</span>}
          {r.out_of_range && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700" data-testid={`too-far-${r.restaurant_id}`}>Too far to deliver</span>}
          {r.open_now && r.eta_min != null && <span className="inline-flex items-center gap-1 rounded-full bg-coral-50 px-2 py-0.5 font-semibold text-coral-600" data-testid={`eta-${r.restaurant_id}`}><Navigation className="h-3.5 w-3.5" />{r.eta_min} min</span>}
          <span className="inline-flex items-center gap-1"><Bike className="h-3.5 w-3.5" />Delivery from {inr(30)}</span>
          {hours && <span className="inline-flex items-center gap-1 text-ink-soft"><Clock className="h-3.5 w-3.5" />{hours}</span>}
          {!r.open_now && r.next_open_label && <span className="inline-flex items-center gap-1 font-medium text-brand" data-testid={`card-reopen-${r.restaurant_id}`}><Clock className="h-3.5 w-3.5" />{r.next_open_label}</span>}
          {r.open_now && r.accepting_orders === false && r.next_open_label && <span className="inline-flex items-center gap-1 font-semibold text-amber-700" data-testid={`card-paused-${r.restaurant_id}`}><Clock className="h-3.5 w-3.5" />{r.next_open_label}</span>}
        </div>
      </div>
    </>
  );
  if (closed) {
    return (
      <div className="kt-card group cursor-not-allowed overflow-hidden opacity-80" data-testid={`restaurant-card-${r.restaurant_id}`} title="Closed right now — you can order once it reopens">
        {inner}
      </div>
    );
  }
  return (
    <Link to={`/app/food/${r.restaurant_id}`} className="kt-card kt-card-hover group overflow-hidden" data-testid={`restaurant-card-${r.restaurant_id}`}>
      {inner}
    </Link>
  );
}

export default function Food() {
  const { location, locating, setLocation, requestCurrentLocation, openPrompt } = useLocationCtx();
  const [restaurants, setRestaurants] = useState(null);
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("all");
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [favs, setFavs] = useState([]);
  const [launchOffer, setLaunchOffer] = useState(null);

  useEffect(() => { foodApi.launchOffer().then(setLaunchOffer).catch(() => {}); }, []);

  useEffect(() => { favApi.list().then((d) => setFavs(d.favourites || [])).catch(() => {}); }, []);

  useEffect(() => { addressApi.list().then((l) => setSavedAddrs((l || []).filter((a) => Number.isFinite(a.lat)))).catch(() => {}); }, []);

  useEffect(() => {
    setRestaurants(null);
    const params = location?.lat != null ? { lat: location.lat, lng: location.lng } : {};
    foodApi.restaurants(params).then(setRestaurants).catch(() => setRestaurants([]));
  }, [location]);

  const onUseCurrent = () => { requestCurrentLocation().catch(() => openPrompt()); };

  const cuisines = useMemo(() => {
    const set = new Set();
    (restaurants || []).forEach((r) => {
      const list = r.cuisines && r.cuisines.length ? r.cuisines : (r.cuisine ? [r.cuisine] : []);
      list.forEach((c) => c && set.add(c));
    });
    return ["all", ...Array.from(set).sort()];
  }, [restaurants]);

  const rCuisines = (r) => (r.cuisines && r.cuisines.length ? r.cuisines : (r.cuisine ? [r.cuisine] : []));

  const filtered = useMemo(() => {
    const base = (restaurants || []).filter((r) => {
      const matchQ = !q.trim() || `${r.name} ${rCuisines(r).join(" ")} ${r.description || ""}`.toLowerCase().includes(q.trim().toLowerCase());
      const inRange = r.distance_km == null || r.distance_km <= 10; // only within the 10 km delivery radius
      return matchQ && (cuisine === "all" || rCuisines(r).includes(cuisine)) && inRange;
    });
    return [...base].sort((a, b) => (b.open_now === true) - (a.open_now === true) || ((a.distance_km ?? 1e9) - (b.distance_km ?? 1e9)));
  }, [restaurants, q, cuisine]);

  const headline = "Restaurants near you";

  return (
    <>
      <PageHeader
        title="Food"
        description="Order from local kitchens. Our delivery partners bring it to your door."
        actions={<Button asChild variant="outline" className="rounded-full"><Link to="/app/orders" data-testid="link-orders"><ReceiptText className="mr-2 h-4 w-4" /> My orders</Link></Button>}
      />
      {launchOffer?.active && (() => {
        const cd = offerCountdown(launchOffer.ends);
        return (
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 overflow-hidden rounded-2xl bg-gradient-to-r from-brand to-coral-600 px-4 py-3 text-white shadow-sm" data-testid="launch-offer-banner">
            <Sparkles className="h-5 w-5 shrink-0" />
            <p className="text-sm font-extrabold tracking-tight sm:text-base">{launchOffer.percent}% OFF every food order</p>
            <span className="hidden h-3.5 w-px bg-white/40 sm:inline-block" />
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/95 sm:text-sm" data-testid="launch-offer-countdown">
              <Timer className="h-4 w-4" /> Offer ends {cd?.label}{cd && cd.daysLeft > 0 ? ` · ${cd.daysLeft} day${cd.daysLeft > 1 ? "s" : ""} left` : ""}
            </p>
            <span className="ml-auto rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide">Auto-applied</span>
          </div>
        );
      })()}
      <div className="mb-4 rounded-2xl border border-surface-300 bg-white/70 px-4 py-2.5 text-sm" data-testid="location-bar">
        <div className="flex flex-wrap items-center gap-2">
          <MapPin className="h-4 w-4 text-brand" />
          <span className="text-ink-soft">{location?.label ? <>Delivering to <strong className="text-ink">{location.label}</strong> · nearest first</> : "Set your location to see the closest kitchens and drivers"}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={openPrompt} className="rounded-full" data-testid="change-location">Change</Button>
            <Button type="button" variant="outline" size="sm" onClick={onUseCurrent} disabled={locating} className="rounded-full" data-testid="use-current-location">
              {locating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Navigation className="mr-1.5 h-3.5 w-3.5" />} Use current location
            </Button>
          </div>
        </div>
        {savedAddrs.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-surface-200 pt-2" data-testid="saved-place-chips">
            <span className="text-xs text-ink-muted">Saved:</span>
            {savedAddrs.map((a) => (
              <button key={a.address_id || a.id || a.label} type="button" onClick={() => setLocation({ lat: a.lat, lng: a.lng, label: a.label || a.address })} className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-3 py-1 text-xs font-medium text-ink-soft hover:text-ink" data-testid={`saved-place-${(a.label || "").toLowerCase()}`}>
                <MapPin className="h-3 w-3" />{a.label || "Saved"}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search restaurants or cuisines" className="h-11 bg-white pl-9" data-testid="food-search" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 kt-scrollbar" data-testid="cuisine-filters">
          {cuisines.map((c) => (
            <button key={c} onClick={() => setCuisine(c)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${cuisine === c ? "bg-brand text-white" : "bg-white text-ink-soft ring-1 ring-surface-300 hover:text-ink"}`} data-testid={`cuisine-${c}`}>
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-6 rounded-2xl border border-surface-300 bg-white/70 p-4" data-testid="smart-food-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-ink" data-testid="meal-headline">{headline}</p>
            <p className="text-xs text-ink-muted">Showing kitchens near you — closed ones are marked.</p>
          </div>
        </div>
        <div className="mt-3">
          <p className="text-xs text-ink-muted">Restaurants within 10 km, open ones first.</p>
        </div>
      </div>
      {favs.length > 0 && (
        <div className="mb-8" data-testid="saved-restaurants">
          <div className="mb-3 flex items-center gap-2">
            <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
            <h2 className="text-base font-bold text-ink">Saved restaurants</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {favs.map((r) => <RestaurantCard key={r.restaurant_id} r={r} />)}
          </div>
        </div>
      )}
      {restaurants === null && <LoadingBlock rows={3} />}
      {restaurants && filtered.length === 0 && <EmptyState icon={UtensilsCrossed} title="No restaurants found" text="Try a different search, budget or cuisine." />}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="restaurants-grid">
        {filtered.map((r) => <RestaurantCard key={r.restaurant_id} r={r} />)}
      </div>
    </>
  );
}
